/**
 * Faraja Media — Home.tsx (Final Production Version)
 * ──────────────────────────────────────────────────────────────
 * Creative heading: "Helping You Be Present"
 * SEO line: "Funeral Livestreaming & Memorial Services in Nairobi" (subtle)
 * All styles inline, uses official brand colours (#C9A84C, #0A0A0A, etc.)
 * No Tailwind, no extra configuration. Works with your existing setup.
 */

import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { SEO } from '../components/SEO';
import { lazy, Suspense } from 'react';
import WhatsAppIcon from '../components/WhatsAppIcon';
// AIAssistant removed to optimize homepage performance (moved to booking route)
import {
  Radio, Camera, Video, Plane, Star, ArrowRight,
  Globe, MessageCircle, Phone, ChevronRight,
  Info, X, ChevronDown, MapPin, Check, Smartphone, Mail,
} from 'lucide-react';

// SEO meta data for Home page
const HOME_SEO = {
  title: 'Funeral Livestreaming & Memorial Services Nairobi Kenya | Faraja Media',
  description: 'Professional funeral livestreaming, memorial photography, and cinematic video production services in Nairobi, Kenya. Serving diaspora families worldwide with 24/7 support and guaranteed quality.',
  ogImage: 'https://images.unsplash.com/photo-1516035069371-29a1b244cc32?auto=format&fit=crop&q=80&w=1200',
  serviceType: 'livestreaming' as const,
  breadcrumbs: [
    { name: 'Home', url: '/' },
    { name: 'Funeral Services', url: '/services' }
  ]
};

// Enhanced schema for home page
const homePageSchema = [
  {
    "@type": "WebPage",
    "name": "Faraja Media - Funeral Livestreaming & Memorial Services",
    "description": "Professional funeral livestreaming, memorial photography, cinematic video production and drone coverage services in Nairobi, Kenya. Serving diaspora families worldwide.",
    "url": "https://farajamedia.com/",
    "mainEntity": {
      "@type": "Organization",
      "name": "Faraja Media",
      "url": "https://farajamedia.com",
      "logo": "https://images.pixieset.com/492772111/2c35f543e5662c954e9dea8a7381893c-xxlarge.png",
      "telephone": "+254721296564",
      "email": "farajafuneral@gmail.com",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "ABC Place, Westlands",
        "addressLocality": "Nairobi",
        "addressRegion": "Nairobi County",
        "postalCode": "00100",
        "addressCountry": "KE"
      },
      "openingHours": [
        "Mo-Fr 06:00-22:00",
        "Sa-Su 06:00-22:00"
      ],
      "priceRange": "KSh 20,000 – KSh 150,000",
      "paymentAccepted": ["M-Pesa", "Bank Transfer", "Visa", "Mastercard", "PayPal"],
      "areaServed": [
        "Nairobi, Kenya",
        "Mombasa, Kenya",
        "Kisumu, Kenya",
        "Nakuru, Kenya",
        "UK, USA, Canada, Australia (Diaspora)"
      ]
    }
  }
];

/* ─── Official Brand Colours (inline style object) ─── */
const V = {
  gold: '#C9A84C',
  goldHover: '#D4B85A',
  goldDeep: '#8B6914',
  goldBg: 'rgba(201,168,76,0.08)',
  goldBorder: 'rgba(201,168,76,0.25)',
  bg: '#0A0A0A',
  card: '#111111',
  surface: '#1A1A1A',
  deep: '#0D0D0D',
  ink: '#F5F5F0',
  ink2: 'rgba(245, 245, 240, 0.85)',
  ink3: 'rgba(245, 245, 240, 0.70)',
  border: 'rgba(245,245,240,0.07)',
  wa: '#25D366',
  fontDisplay: "'Cormorant Garamond', Georgia, serif",
  fontBody: "'DM Sans', sans-serif",
};

const TRUST_STATS = {
  familiesServed: '500+',
  countries: '15+',
  responseTime: '< 10 min',
  rating: '4.9★',
};

const SOCIAL_LINKS = {
  googleReview: 'https://g.page/r/farajamedia/review',
  whatsapp: 'https://wa.me/254721296564',
  facebook: 'https://www.facebook.com/FarajaMedia/',
  youtube: 'https://www.youtube.com/@thefarajamedia',
  linkedin: 'https://www.linkedin.com/company/farajamedia',
};

const WHATSAPP_LINK = SOCIAL_LINKS.whatsapp;
const PHONE = '+254 721 296 564';
const HOME_FAQS = [
  { q: 'How does funeral live streaming work in Kenya?', a: 'We set up broadcast-quality multi-camera rigs with professional audio at the venue. A private, secure streaming link is generated — viewable on any device in any country, simultaneously. The stream is recorded and available for download after the service.' },
  { q: 'Can you stream to family in the UK, USA, Canada, or Australia?', a: 'Absolutely. We have successfully streamed to families in 15+ countries with zero interruptions. Our infrastructure handles unlimited concurrent viewers and delivers HD quality regardless of the viewer\'s location or device.' },
  { q: 'What does funeral photography in Nairobi cost?', a: 'Photography starts from KSh 20,000 for standard coverage. Final pricing depends on event duration, location, and bundled services. Use our Package Builder above for an instant personalised estimate.' },
  { q: 'How quickly can you respond for same-day emergency bookings?', a: 'We offer 24/7 emergency response. WhatsApp us at +254 721 296 564 and we confirm availability within 30 minutes. For urgent same-day coverage in Nairobi, our team can deploy within 2 hours of booking confirmation.' },
  { q: 'Do you travel outside Nairobi for events?', a: 'Yes. We regularly cover events across all of Kenya — Mombasa, Kisumu, Nakuru, Eldoret, Kiambu, and rural areas. Travel fees apply for locations outside Nairobi. Contact us with your specific location for a full quote.' },
  { q: 'Can you coordinate everything if I\'m in the diaspora?', a: 'This is one of our specialties. Our diaspora coordinators handle communication with the funeral home, venue, and local family on your behalf. You are given one dedicated point of contact who manages everything from end to end.' },
  { q: 'What payment methods do you accept?', a: 'We accept M-Pesa (Paybill 247247, Account 502401 — CGEN-PIX CREATIVES), bank transfer, Visa, Mastercard, and PayPal for international clients. No payment is required upfront — a deposit is taken once we confirm availability.' },
  { q: 'What is included in the cinematic video package?', a: 'Multi-camera coverage, professional colour grading, editorial-style assembly cut, licensed background music, full-length ceremony edit, and a short tribute highlight reel. Delivered as downloadable HD files and a private online gallery.' },
  { q: 'Do you offer drone coverage for funerals in Kenya?', a: 'Yes. Our licensed drone pilots provide 4K cinematic aerial coverage for outdoor funeral ceremonies and burials. Drone coverage requires advance notice and may be subject to airspace restrictions at certain venues — contact us to confirm for your location.' },
];

/* ═══════════════════════════════════════════════════════════════
   SHARED COMPONENTS
   ═══════════════════════════════════════════════════════════════ */

// Unified WhatsAppIcon imported from components

function Eyebrow({ children }: { children: React.ReactNode }) {
  return (
    <span style={{
      display: 'inline-block',
      fontSize: '10px',
      fontWeight: 700,
      letterSpacing: '0.3em',
      textTransform: 'uppercase' as const,
      color: V.gold,
      marginBottom: '16px',
    }}>{children}</span>
  );
}

function SectionHeading({ children, center = false }: { children: React.ReactNode; center?: boolean }) {
  return (
    <h2 style={{
      fontFamily: V.fontDisplay,
      fontSize: 'clamp(2.2rem, 5vw, 4rem)',
      fontWeight: 500,
      color: V.ink,
      lineHeight: 1.05,
      margin: '0 0 16px',
      textAlign: center ? 'center' : 'left',
    }}>{children}</h2>
  );
}

/* ═══════════════════════════════════════════════════════════════
   1. URGENCY BAR
   ═══════════════════════════════════════════════════════════════ */
function UrgencyBar() {
  const messages = [
    'Available 24/7 for same-day funeral coverage — WhatsApp us now',
    'Connecting Kenyan diaspora in 15+ countries via live stream',
    'No payment required to check availability — response in under 10 min',
  ];
  const [idx, setIdx] = useState(0);
  const [visible, setVisible] = useState(true);

  useEffect(() => {
    const t = setInterval(() => setIdx(i => (i + 1) % messages.length), 4000);
    return () => clearInterval(t);
  }, []);

  if (!visible) return null;

  return (
    <div style={{
      background: V.bg,
      borderBottom: `1px solid ${V.goldBorder}`,
      padding: '10px 20px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '16px',
      position: 'relative',
      zIndex: 200,
      minHeight: '40px' // Reserved height to prevent CLS
    }}>
      <AnimatePresence mode="wait">
        <motion.span
          key={idx}
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -6 }}
          transition={{ duration: 0.35 }}
          style={{
            fontSize: '12px',
            fontWeight: 600,
            color: V.ink,
            letterSpacing: '0.04em',
            textAlign: 'center',
          }}
        >
          {messages[idx]}
        </motion.span>
      </AnimatePresence>

      <a
        href={WHATSAPP_LINK}
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Chat on WhatsApp now"
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '6px',
          background: V.gold,
          color: V.bg,
          fontSize: '10px',
          fontWeight: 700,
          padding: '12px 18px',
          minWidth: '48px',
          minHeight: '48px',
          borderRadius: '20px',
          textDecoration: 'none',
          whiteSpace: 'nowrap',
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
          flexShrink: 0,
        }}
      >
        <WhatsAppIcon size={14} color={V.bg} />
        Chat Now
      </a>

      <button
        onClick={() => setVisible(false)}
        aria-label="Dismiss announcement"
        style={{
          position: 'absolute',
          right: '8px',
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          color: V.ink,
          opacity: 0.95,
          padding: '12px',
          minWidth: '48px',
          minHeight: '48px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        <X size={18} />
      </button>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
   2. HERO (creative heading + subtle SEO line)
   ═══════════════════════════════════════════════════════════════ */
function HeroSection() {
  return (
    <section style={{ position: 'relative', height: '100svh', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
      <div style={{ position: 'absolute', inset: 0, zIndex: 0, aspectRatio: '16/9', overflow: 'hidden' }}>
        <img
          src="https://images.unsplash.com/photo-1516035069371-29a1b244cc32?auto=format&fit=crop&q=45&w=800"
          alt=""
          width="800"
          height="450"
          loading="eager"
          fetchPriority="high"
          style={{ width: '100%', height: '100%', objectFit: 'cover', opacity: 0.38 }}
        />
        <div style={{ position: 'absolute', inset: 0, background: `linear-gradient(to bottom, ${V.bg}BF 0%, ${V.bg}59 50%, ${V.bg} 100%)` }} />
      </div>

      <div style={{ position: 'relative', zIndex: 10, maxWidth: '900px', margin: '0 auto', padding: '0 24px', textAlign: 'center' }}>
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
        >
          <span style={{
            display: 'inline-block',
            fontSize: '10px',
            fontWeight: 700,
            letterSpacing: '0.3em',
            textTransform: 'uppercase',
            color: V.gold,
            background: V.goldBg,
            border: `1px solid ${V.goldBorder}`,
            padding: '10px 24px',
            borderRadius: '40px',
            marginBottom: '32px',
          }}>
            Est. 2009 · East Africa's Leader in Memorial Media
          </span>

          {/* MAIN HEADING – creative, human */}
          <h1 style={{
            fontFamily: V.fontDisplay,
            fontSize: 'clamp(3.5rem, 10vw, 8rem)',
            fontWeight: 500,
            color: V.ink,
            lineHeight: 0.9,
            letterSpacing: '-0.01em',
            margin: '0 0 28px',
          }}>
            Helping You <br /><em style={{ color: V.gold }}>Be Present.</em>
          </h1>

          {/* SUPPORTING PARAGRAPH – emotional */}
          <p style={{
            fontSize: 'clamp(1rem, 2vw, 1.25rem)',
            maxWidth: '600px',
            margin: '0 auto 24px',
            lineHeight: 1.75,
            color: V.ink2,
          }}>
            When distance separates us, love remains. We bridge the gap between Kenya and the Diaspora with dignified, cinematic memorial coverage.
          </p>

          {/* SEO KEYWORD LINE – subtle, not crowded */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '12px',
            marginBottom: '32px',
          }}>
            <div style={{ width: '30px', height: '1px', background: V.goldBorder }} />
            <p style={{
              fontSize: '11px',
              letterSpacing: '0.2em',
              textTransform: 'uppercase',
              color: V.ink3,
              margin: 0,
            }}>
              Funeral Livestreaming & Memorial Services in Nairobi
            </p>
            <div style={{ width: '30px', height: '1px', background: V.goldBorder }} />
          </div>

          {/* CTA BUTTONS */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px', justifyContent: 'center' }}>
            <a
              href={WHATSAPP_LINK}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                background: V.gold,
                color: V.bg,
                padding: '16px 36px',
                borderRadius: '6px',
                textDecoration: 'none',
                fontSize: '12px',
                fontWeight: 700,
                letterSpacing: '0.15em',
                textTransform: 'uppercase',
              }}
            >
              Book via WhatsApp <ArrowRight size={14} />
            </a>
            <Link
              to="/services"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                background: 'transparent',
                color: V.ink,
                padding: '16px 36px',
                borderRadius: '6px',
                textDecoration: 'none',
                fontSize: '12px',
                fontWeight: 700,
                letterSpacing: '0.15em',
                textTransform: 'uppercase',
                border: `1px solid ${V.border}`,
              }}
            >
              Explore Services
            </Link>
          </div>
        </motion.div>
      </div>

      <motion.div
        animate={{ y: [0, 10, 0] }}
        transition={{ repeat: Infinity, duration: 3, ease: 'easeInOut' }}
        style={{ position: 'absolute', bottom: '40px', left: '50%', transform: 'translateX(-50%)' }}
      >
        <div style={{ width: '1px', height: '64px', background: `linear-gradient(to bottom, ${V.gold}, transparent)`, margin: '0 auto', opacity: 0.5 }} />
      </motion.div>
    </section>
  );
}

/* ═══════════════════════════════════════════════════════════════
   3. TRUST STATS
   ═══════════════════════════════════════════════════════════════ */
function TrustStats() {
  const stats = [
    { number: TRUST_STATS.familiesServed, label: 'Families Served', sub: 'Since 2009' },
    { number: TRUST_STATS.countries, label: 'Countries', sub: 'Global Reach' },
    { number: TRUST_STATS.responseTime, label: 'Response Time', sub: '24/7 Support' },
    { number: TRUST_STATS.rating, label: 'Google Rating', sub: 'Verified Reviews' },
  ];

  return (
    <section style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 24px' }}>
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
        gap: '0',
        borderTop: `1px solid ${V.border}`,
        borderBottom: `1px solid ${V.border}`,
        padding: '56px 0',
        margin: '0 0 120px',
      }}>
        {stats.map((s) => (
          <div key={s.label} style={{ textAlign: 'center', padding: '0 24px' }}>
            <p style={{
              fontFamily: V.fontDisplay,
              fontSize: 'clamp(2.5rem, 5vw, 4rem)',
              color: V.ink,
              margin: '0 0 8px',
              lineHeight: 1,
            }}>{s.number}</p>
            <p style={{ fontSize: '12px', fontWeight: 700, letterSpacing: '0.2em', textTransform: 'uppercase', color: V.ink, margin: '0 0 4px' }}>{s.label}</p>
            <p style={{ fontSize: '10px', letterSpacing: '0.2em', textTransform: 'uppercase', color: V.ink3 }}>{s.sub}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

/* ═══════════════════════════════════════════════════════════════
   4. SERVICES / PRICING with currency toggle
   ═══════════════════════════════════════════════════════════════ */
function ServicesSection() {
  const [currency, setCurrency] = useState<'KSH' | 'USD' | 'GBP'>('KSH');
  const rates = { KSH: 1, USD: 0.0078, GBP: 0.0061 };

  const fmt = (ksh: number) => {
    const val = Math.round(ksh * rates[currency]);
    if (currency === 'KSH') return `KSh ${val.toLocaleString()}`;
    if (currency === 'USD') return `$${val.toLocaleString()}`;
    return `£${val.toLocaleString()}`;
  };

  const services = [
    {
      icon: <Radio size={32} />,
      title: 'Live Funeral Streaming',
      sub: 'Nairobi, Kenya',
      image: '/images/optimized/Funeral Live Streaming.webp',
      price: 20000,
      features: ['Private streaming link', 'Unlimited viewers worldwide', 'Full HD recording included'],
    },
    {
      icon: <Camera size={32} />,
      title: 'Memorial Photography',
      sub: 'East Africa',
      image: '/images/optimized/Funeral Photography.webp',
      price: 20000,
      features: ['Professional editing', 'Private online gallery', 'High-res downloads'],
    },
    {
      icon: <Video size={32} />,
      title: 'Cinematic Tribute Video',
      sub: 'Kenya Diaspora',
      image: '/images/optimized/Funeral videography.webp',
      price: 20000,
      features: ['Multi-camera coverage', 'Cinema color grade', '4K delivery'],
    },
    {
      icon: <Plane size={32} />,
      title: 'Drone Aerial Coverage',
      sub: 'All of Kenya',
      image: '/images/optimized/Funeral Drone Photography.webp',
      price: 25000,
      features: ['Licensed FAA-grade pilots', '4K aerial footage', 'Perfect for outdoor ceremonies'],
    },
  ];

  return (
    <section style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 24px 120px' }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-end', justifyContent: 'space-between', gap: '24px', marginBottom: '56px' }}>
        <div>
          <Eyebrow>Our Services</Eyebrow>
          <SectionHeading>What we <em>offer</em></SectionHeading>
        </div>

        <div style={{ display: 'flex', gap: '6px', background: V.surface, padding: '6px', borderRadius: '12px', border: `1px solid ${V.border}` }}>
          {(['KSH', 'USD', 'GBP'] as const).map(c => (
            <button
              key={c}
              onClick={() => setCurrency(c)}
              aria-label={`Switch currency to ${c}`}
              style={{
                padding: '12px 24px',
                minWidth: '48px',
                minHeight: '48px',
                borderRadius: '8px',
                border: 'none',
                background: currency === c ? V.gold : 'transparent',
                color: currency === c ? V.bg : V.ink3,
                fontSize: '11px',
                fontWeight: 700,
                letterSpacing: '0.12em',
                cursor: 'pointer',
                transition: 'all 0.15s',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >{c}</button>
          ))}
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: '20px' }}>
        {services.map((s) => (
          <PricingCard key={s.title} {...s} price={fmt(s.price)} />
        ))}
      </div>
    </section>
  );
}

function PricingCard({ icon, title, sub, image, price, features }: any) {
  return (
    <div style={{
      background: V.card,
      border: `1px solid ${V.border}`,
      borderRadius: '24px',
      overflow: 'hidden',
      display: 'flex',
      flexDirection: 'column',
      transition: 'border-color 0.2s',
    }}
      onMouseEnter={e => (e.currentTarget.style.borderColor = V.goldBorder)}
      onMouseLeave={e => (e.currentTarget.style.borderColor = V.border)}
    >
      <div style={{
        position: 'relative',
        width: '100%',
        paddingBottom: '62.5%',
        overflow: 'hidden',
        background: '#151515'
      }}>
        <img
          src={image}
          alt=""
          loading="lazy"
          decoding="async"
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            transition: 'transform 2s',
            display: 'block'
          }}
          onMouseEnter={e => ((e.target as HTMLImageElement).style.transform = 'scale(1.06)')}
          onMouseLeave={e => ((e.target as HTMLImageElement).style.transform = 'scale(1)')}
        />
      </div>
      <div style={{ padding: '28px', display: 'flex', flexDirection: 'column', flex: 1 }}>
        <div style={{ color: V.gold, marginBottom: '14px' }}>{icon}</div>
        <p style={{ fontFamily: V.fontDisplay, fontSize: '22px', color: V.ink, margin: '0 0 2px', fontWeight: 500 }}>{title}</p>
        <p style={{ fontSize: '11px', color: V.ink3, letterSpacing: '0.15em', textTransform: 'uppercase', margin: '0 0 16px' }}>{sub}</p>
        <p style={{ fontFamily: V.fontDisplay, fontSize: '32px', color: V.gold, margin: '0 0 16px', fontWeight: 700 }}>{price}</p>
        <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 24px', display: 'flex', flexDirection: 'column', gap: '8px', flex: 1 }}>
          {features.map((f: string) => (
            <li key={f} style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '13px', color: V.ink2 }}>
              <div style={{ width: '5px', height: '5px', borderRadius: '50%', background: V.gold, opacity: 0.6, flexShrink: 0 }} />
              {f}
            </li>
          ))}
        </ul>
        <a
          href={WHATSAPP_LINK}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            padding: '14px',
            border: `1px solid ${V.border}`,
            borderRadius: '10px',
            color: V.ink2,
            textDecoration: 'none',
            fontSize: '12px',
            fontWeight: 700,
            letterSpacing: '0.1em',
            textTransform: 'uppercase',
            transition: 'all 0.15s',
          }}
          onMouseEnter={e => {
            const el = e.currentTarget as HTMLAnchorElement;
            el.style.background = V.gold;
            el.style.color = V.bg;
            el.style.borderColor = V.gold;
          }}
          onMouseLeave={e => {
            const el = e.currentTarget as HTMLAnchorElement;
            el.style.background = 'transparent';
            el.style.color = V.ink2;
            el.style.borderColor = V.border;
          }}
        >
          Select Package <ArrowRight size={14} />
        </a>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
   5. DIASPORA MODULE
   ═══════════════════════════════════════════════════════════════ */
function DiasporaSection() {
  return (
    <section style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 24px 80px' }}>
      <div style={{
        background: V.card,
        border: `1px solid ${V.border}`,
        borderRadius: '40px',
        padding: 'clamp(32px, 5vw, 64px)',
        position: 'relative',
        overflow: 'hidden',
      }}>
        <div style={{ position: 'absolute', top: 0, right: 0, padding: '48px', opacity: 0.02, pointerEvents: 'none' }}>
          <Globe size={400} color={V.gold} />
        </div>

        <div style={{ position: 'relative', zIndex: 1 }}>
          <Eyebrow>Diaspora Support</Eyebrow>
          <SectionHeading>Coordinating from <em>Abroad?</em></SectionHeading>
          <p style={{ fontSize: '16px', color: V.ink2, lineHeight: 1.8, maxWidth: '560px', margin: '0 0 40px' }}>
            We specialise in helping diaspora families in the UK, USA, Canada, Australia and the Middle East manage funeral and memorial logistics in Kenya with absolute transparency and care.
          </p>

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '20px', marginBottom: '20px' }}>
            {[
              { title: 'Death Certificate Guidance', desc: 'Step-by-step help with Kenyan civil registration.' },
              { title: 'Burial Transit Permits', desc: 'Coordinating with airlines and local authorities.' },
              { title: 'Cross-Border Payments', desc: 'Pay via Visa, Mastercard, PayPal, or M-Pesa.' },
              { title: 'Time Zone Coordination', desc: 'Available across all time zones — 24/7.' },
            ].map(item => (
              <div key={item.title} style={{ display: 'flex', gap: '12px', alignItems: 'flex-start', flex: '1 1 220px' }}>
                <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: V.gold, marginTop: '7px', flexShrink: 0 }} />
                <div>
                  <p style={{ fontSize: '14px', fontWeight: 600, color: V.ink, margin: '0 0 3px' }}>{item.title}</p>
                  <p style={{ fontSize: '13px', color: V.ink3, margin: 0, lineHeight: 1.5 }}>{item.desc}</p>
                </div>
              </div>
            ))}
          </div>

          <a
            href={WHATSAPP_LINK}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              background: V.wa,
              color: '#0A0908',
              padding: '14px 28px',
              borderRadius: '8px',
              textDecoration: 'none',
              fontSize: '12px',
              fontWeight: 700,
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
            }}
          >
            <WhatsAppIcon size={15} color="#0A0908" />
            Get Diaspora Support
          </a>
        </div>
      </div>
    </section>
  );
}

/* ═══════════════════════════════════════════════════════════════
   6. DIASPORA GUIDE (How It Works)
   ═══════════════════════════════════════════════════════════════ */
function DiasporaGuide() {
  const steps = [
    { n: '01', title: 'Contact us first', body: "WhatsApp or call us from anywhere in the world. We confirm availability within 10 minutes and assign a dedicated coordinator to your family — one point of contact for everything." },
    { n: '02', title: 'We handle the ground', body: "Our Nairobi team coordinates with the funeral home, venue, and your local family members. You don't need to be in Kenya to manage any of this." },
    { n: '03', title: 'Your family watches live', body: "We send a private streaming link. Unlimited viewers. HD quality on any device. The full service is recorded and downloadable after the ceremony." },
  ];

  return (
    <section style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 24px 80px' }}>
      <div style={{ textAlign: 'center', marginBottom: '56px' }}>
        <Eyebrow>How it works — from abroad</Eyebrow>
        <SectionHeading center>Three steps.<br /><em style={{ color: V.gold }}>That's all it takes.</em></SectionHeading>
        <p style={{ fontSize: '15px', color: V.ink2, maxWidth: '500px', margin: '0 auto', lineHeight: 1.8 }}>We've guided 500+ diaspora families through this process. It's simpler than you think.</p>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '20px', marginBottom: '48px' }}>
        {steps.map(s => (
          <div key={s.n} style={{ background: V.card, border: `1px solid ${V.border}`, borderRadius: '24px', padding: '36px 28px' }}>
            <span aria-hidden="true" style={{ display: 'block', fontFamily: V.fontDisplay, fontSize: '64px', fontWeight: 700, color: V.gold, opacity: 0.6, lineHeight: 1, marginBottom: '12px' }}>{s.n}</span>
            <h3 style={{ fontFamily: V.fontDisplay, fontSize: '22px', color: V.ink, margin: '0 0 10px', fontWeight: 500 }}>{s.title}</h3>
            <p style={{ fontSize: '14px', color: V.ink2, lineHeight: 1.8, margin: 0 }}>{s.body}</p>
          </div>
        ))}
      </div>

      <div style={{ background: V.goldBg, border: `1px solid ${V.goldBorder}`, borderRadius: '24px', padding: '40px 44px', position: 'relative' }}>
        <div style={{ position: 'absolute', top: '-14px', left: '32px', background: V.gold, borderRadius: '50%', width: '44px', height: '44px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Info size={22} color={V.bg} />
        </div>
        <blockquote style={{ fontFamily: V.fontDisplay, fontSize: 'clamp(1.1rem, 2vw, 1.5rem)', color: V.ink, fontStyle: 'italic', lineHeight: 1.6, margin: '0 0 16px' }}>
          "Faraja Media made us feel like we were right there in Nairobi, even though we were in London. The clarity of the stream was incredible."
        </blockquote>
        <p style={{ fontSize: '10px', fontWeight: 700, letterSpacing: '0.3em', textTransform: 'uppercase', color: V.gold, margin: 0 }}>— Sarah M., UK Diaspora</p>
      </div>
    </section>
  );
}

/* ═══════════════════════════════════════════════════════════════
   7. PACKAGE BUILDER (interactive)
   ═══════════════════════════════════════════════════════════════ */
function PackageBuilder() {
  const [currency, setCurrency] = useState<'KSH' | 'USD' | 'GBP'>('KSH');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [hours, setHours] = useState(4);
  const [crew, setCrew] = useState(2);

  const rates = { KSH: 1, USD: 0.0078, GBP: 0.0061 };
  const sym = { KSH: 'KSh', USD: '$', GBP: '£' };
  const fmt = (ksh: number) => `${sym[currency]} ${Math.round(ksh * rates[currency]).toLocaleString()}`;

  const services = [
    { id: 'photo', label: 'Event Photography', base: 15000, perHour: 2000, icon: '📷' },
    { id: 'video', label: 'Cinematic Videography', base: 25000, perHour: 3000, icon: '🎬' },
    { id: 'stream', label: 'Live Streaming', base: 20000, perHour: 0, icon: '📡' },
    { id: 'drone', label: 'Drone Aerial Coverage', base: 15000, perHour: 2500, icon: '🚁' },
    { id: 'tribute', label: 'Cinematic Tribute Film', base: 35000, perHour: 0, icon: '🎞️' },
    { id: 'edit', label: 'Same-Day Edit Reel', base: 18000, perHour: 0, icon: '⚡' },
  ];

  const toggle = (id: string) => {
    setSelected(prev => {
      const n = new Set(prev);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });
  };

  const extraHours = Math.max(0, hours - 4);
  const subtotal = Array.from(selected).reduce((sum, id) => {
    const s = services.find(x => x.id === id)!;
    return sum + s.base + s.perHour * extraHours;
  }, 0);
  const crewExtra = crew > 2 ? (crew - 2) * 8000 : 0;
  const total = subtotal + crewExtra;
  const hasBundle = selected.size >= 3;
  const discount = hasBundle ? Math.round(total * 0.1) : 0;
  const final = total - discount;

  const sendToWA = () => {
    const names = Array.from(selected).map(id => services.find(s => s.id === id)!.label);
    const msg = encodeURIComponent(
      `Hello Faraja Media,\n\nI'd like to book:\n${names.map(n => `• ${n}`).join('\n')}\n\nDuration: ${hours}h | Crew: ${crew}\nEstimate: ${fmt(final)}\n\nPlease confirm availability.`
    );
    window.open(`https://wa.me/254721296564?text=${msg}`, '_blank');
  };

  return (
    <section style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 24px 120px' }}>
      <div style={{ textAlign: 'center', marginBottom: '56px' }}>
        <Eyebrow>Build Your Package</Eyebrow>
        <SectionHeading center>Price your coverage<br /><em style={{ color: V.gold }}>in 60 seconds.</em></SectionHeading>
        <p style={{ fontSize: '15px', color: V.ink2, maxWidth: '480px', margin: '0 auto', lineHeight: 1.8 }}>Select services, set your event duration, and send the quote directly to our WhatsApp — no phone call needed.</p>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) 320px', gap: '28px', alignItems: 'start' }}>
        {/* Left */}
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '24px' }}>
            <span style={{ fontSize: '12px', color: V.ink3 }}>Show prices in:</span>
            {(['KSH', 'USD', 'GBP'] as const).map(c => (
              <button key={c} onClick={() => setCurrency(c)} aria-label={`Switch price to ${c}`} style={{
                padding: '12px 18px', minWidth: '48px', minHeight: '44px', borderRadius: '6px', border: `1px solid ${currency === c ? V.gold : V.border}`,
                background: currency === c ? V.gold : 'transparent', color: currency === c ? V.bg : V.ink, // Boosted contrast to V.ink
                fontSize: '11px', fontWeight: 700, letterSpacing: '0.1em', cursor: 'pointer', transition: 'all 0.15s',
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center'
              }}>{c}</button>
            ))}
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(170px, 1fr))', gap: '12px', marginBottom: '28px' }}>
            {services.map(s => {
              const on = selected.has(s.id);
              return (
                <button key={s.id} onClick={() => toggle(s.id)} style={{
                  background: on ? V.goldBg : V.card,
                  border: `1.5px solid ${on ? V.gold : V.border}`,
                  borderRadius: '16px', padding: '18px 16px',
                  cursor: 'pointer', textAlign: 'left', transition: 'all 0.15s', position: 'relative',
                }}>
                  {on && (
                    <div style={{
                      position: 'absolute', top: '10px', right: '10px',
                      width: '18px', height: '18px', borderRadius: '50%',
                      background: V.gold, display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                      <Check size={10} color={V.bg} />
                    </div>
                  )}
                  <span style={{ fontSize: '24px', display: 'block', marginBottom: '8px' }}>{s.icon}</span>
                  <p style={{ fontSize: '13px', fontWeight: 600, color: on ? V.gold : V.ink, margin: '0 0 4px', lineHeight: 1.3 }}>{s.label}</p>
                  <p style={{ fontSize: '12px', color: V.ink3, margin: 0 }}>from {fmt(s.base)}</p>
                </button>
              );
            })}
          </div>

          <div style={{ background: V.card, border: `1px solid ${V.border}`, borderRadius: '20px', padding: '28px' }}>
            <div style={{ marginBottom: '24px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px' }}>
                <span style={{ fontSize: '13px', color: V.ink2 }}>Event duration</span>
                <span style={{ fontSize: '15px', fontWeight: 600, color: V.ink }}>{hours} <span style={{ fontSize: '12px', color: V.ink3 }}>hrs</span></span>
              </div>
              <input type="range" min={2} max={12} step={1} value={hours}
                onChange={e => setHours(Number(e.target.value))}
                aria-label="Event duration in hours"
                style={{ width: '100%', accentColor: V.gold, height: '44px' }} />
              <p style={{ fontSize: '11px', color: V.gold, margin: '6px 0 0' }}>
                {hours > 4 ? `+${hours - 4}h overtime included in estimate` : 'Base rate covers 4 hours'}
              </p>
            </div>
            <div style={{ height: '1px', background: V.border, margin: '0 0 24px' }} />
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px' }}>
                <span style={{ fontSize: '13px', color: V.ink2 }}>Camera crew size</span>
                <span style={{ fontSize: '15px', fontWeight: 600, color: V.ink }}>{crew} <span style={{ fontSize: '12px', color: V.ink3 }}>people</span></span>
              </div>
              <input type="range" min={1} max={5} step={1} value={crew}
                onChange={e => setCrew(Number(e.target.value))}
                aria-label="Number of camera crew people"
                style={{ width: '100%', accentColor: V.gold, height: '44px' }} />
              <p style={{ fontSize: '11px', color: V.gold, margin: '6px 0 0' }}>
                {crew > 2 ? `Additional crew: ${fmt(crewExtra)}` : 'Standard 2-person crew included'}
              </p>
            </div>
          </div>
        </div>

        {/* Right — live quote */}
        <div style={{
          background: V.card, border: `1px solid ${V.border}`, borderRadius: '24px',
          padding: '28px', position: 'sticky', top: '80px',
        }}>
          <p style={{ fontSize: '10px', fontWeight: 700, letterSpacing: '0.3em', textTransform: 'uppercase', color: V.gold, margin: '0 0 20px' }}>
            Live Quote
          </p>

          {selected.size === 0 ? (
            <p style={{ fontSize: '14px', color: V.ink3, lineHeight: 1.7, margin: '0 0 24px' }}>
              Select at least one service to see your estimate.
            </p>
          ) : (
            <>
              <div style={{ marginBottom: '16px' }}>
                {Array.from(selected).map(id => {
                  const s = services.find(x => x.id === id)!;
                  const cost = s.base + s.perHour * extraHours;
                  return (
                    <div key={id} style={{ display: 'flex', justifyContent: 'space-between', padding: '9px 0', borderBottom: `1px solid ${V.border}` }}>
                      <span style={{ fontSize: '13px', color: V.ink2 }}>{s.label}</span>
                      <span style={{ fontSize: '13px', color: V.ink, fontWeight: 500 }}>{fmt(cost)}</span>
                    </div>
                  );
                })}
                {crewExtra > 0 && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '9px 0', borderBottom: `1px solid ${V.border}` }}>
                    <span style={{ fontSize: '13px', color: V.ink2 }}>Extra crew</span>
                    <span style={{ fontSize: '13px', color: V.ink, fontWeight: 500 }}>{fmt(crewExtra)}</span>
                  </div>
                )}
                {hasBundle && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '9px 0', borderBottom: `1px solid ${V.border}` }}>
                    <span style={{ fontSize: '13px', color: '#5C9E6B' }}>Bundle discount (10%)</span>
                    <span style={{ fontSize: '13px', color: '#5C9E6B', fontWeight: 500 }}>–{fmt(discount)}</span>
                  </div>
                )}
              </div>

              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', margin: '16px 0 8px' }}>
                <span style={{ fontSize: '12px', color: V.ink3 }}>Estimated total</span>
                <span style={{ fontFamily: V.fontDisplay, fontSize: '30px', fontWeight: 700, color: V.gold }}>
                  {fmt(final)}
                </span>
              </div>

              {hasBundle && (
                <div style={{ background: 'rgba(92,158,107,0.08)', border: '1px solid rgba(92,158,107,0.2)', borderRadius: '8px', padding: '10px 14px', marginBottom: '16px' }}>
                  <p style={{ fontSize: '12px', color: '#5C9E6B', margin: 0, fontWeight: 600 }}>
                    Bundle of {selected.size} saves you {fmt(discount)}
                  </p>
                </div>
              )}

              <p style={{ fontSize: '11px', color: V.ink3, margin: '0 0 20px', lineHeight: 1.6 }}>
                Estimate only — final price confirmed by our team before payment.
              </p>
            </>
          )}

          <button
            onClick={sendToWA}
            disabled={selected.size === 0}
            style={{
              width: '100%', padding: '15px', borderRadius: '10px',
              background: selected.size > 0 ? V.wa : V.surface,
              border: 'none', color: selected.size > 0 ? '#fff' : V.ink3,
              fontSize: '12px', fontWeight: 700, letterSpacing: '0.08em',
              textTransform: 'uppercase', cursor: selected.size > 0 ? 'pointer' : 'not-allowed',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
              marginBottom: '10px', transition: 'all 0.15s',
            }}
          >
            <WhatsAppIcon size={15} color={selected.size > 0 ? '#fff' : V.ink3} />
            Send Quote via WhatsApp
          </button>

          <a href="mailto:farajafuneral@gmail.com" style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
            padding: '13px', borderRadius: '10px', background: 'transparent',
            border: `1px solid ${V.border}`, color: V.ink2, fontSize: '12px',
            fontWeight: 600, textDecoration: 'none', letterSpacing: '0.06em',
            textTransform: 'uppercase',
          }}>
            <Mail size={13} /> Email Instead
          </a>

          <div style={{ marginTop: '20px', padding: '16px', background: V.surface, border: `1px solid ${V.border}`, borderRadius: '12px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
              <Smartphone size={13} color={V.gold} />
              <span style={{ fontSize: '12px', fontWeight: 700, color: V.ink }}>M-Pesa Payment</span>
            </div>
            <p style={{ fontSize: '13px', fontWeight: 700, color: V.ink, margin: 0 }}>
              Paybill <span style={{ color: V.gold }}>247247</span> · Account <span style={{ color: V.gold }}>502401</span>
            </p>
            <p style={{ fontSize: '11px', color: V.ink3, margin: '3px 0 0' }}>CGEN-PIX CREATIVES</p>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ═══════════════════════════════════════════════════════════════
   8. PORTFOLIO
   ═══════════════════════════════════════════════════════════════ */
function PortfolioSection() {
  const portfolio = [
    { title: 'Celebrating the Life of M.W. Kibiku — August 2025', img: 'https://images.pixieset.com/76900279/c2e71a2440c63b366ba9cc36f205e0ab-large.jpg', link: 'https://farajamedia.pixieset.com/celebratingthelifeofwanjikukibiku/' },
    { title: 'S.H. Yohondamkoul Funeral Service — February 2025', img: 'https://images.pixieset.com/18268288/86f094c523192905eaac8a6ede5787a8-large.jpg', link: 'https://farajamedia.pixieset.com/sakorhippolytesyohondamkoulfuneralservice/' },
    { title: 'Celebrating the Life of Nzula — December 2024', img: 'https://images.pixieset.com/93073078/b64bf045691df326310e13be3a81c4e2-large.jpg', link: 'https://farajamedia.pixieset.com/celebratingthelifeofnzula/' },
    { title: 'Celebrating Life of PST. M — November 2024', img: 'https://images.pixieset.com/492772111/02c801bae3398c377ecb8e3b2d3aa1d5-large.jpg', link: 'https://farajamedia.pixieset.com/celebratinglifeofpstmusembi/' },
  ];

  return (
    <section style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 24px 120px' }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'flex-end', gap: '16px', marginBottom: '40px' }}>
        <div>
          <Eyebrow>Our Work</Eyebrow>
          <SectionHeading>Our <em>Portfolio</em></SectionHeading>
        </div>
        <a
          href="https://farajamedia.pixieset.com/"
          target="_blank"
          rel="noopener noreferrer"
          style={{ display: 'flex', alignItems: 'center', gap: '8px', color: V.gold, fontSize: '11px', fontWeight: 700, letterSpacing: '0.25em', textTransform: 'uppercase', textDecoration: 'none', borderBottom: `1px solid ${V.goldBorder}`, paddingBottom: '4px' }}
        >
          View Full Gallery <ChevronRight size={16} />
        </a>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '16px' }}>
        {portfolio.map((item) => (
          <a
            key={item.title}
            href={item.link}
            target="_blank"
            rel="noopener noreferrer"
            style={{ display: 'block', aspectRatio: '3/4', borderRadius: '24px', overflow: 'hidden', position: 'relative', border: `1px solid ${V.border}`, textDecoration: 'none' }}
          >
            <img
              src={item.img}
              alt={item.title}
              loading="lazy"
              style={{ width: '100%', height: '100%', objectFit: 'cover', opacity: 0.65, transition: 'opacity 0.4s, transform 2.5s', display: 'block' }}
              onMouseEnter={e => { (e.target as HTMLImageElement).style.opacity = '0.85'; (e.target as HTMLImageElement).style.transform = 'scale(1.06)'; }}
              onMouseLeave={e => { (e.target as HTMLImageElement).style.opacity = '0.65'; (e.target as HTMLImageElement).style.transform = 'scale(1)'; }}
            />
            <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to top, rgba(13,11,9,0.9) 0%, transparent 60%)', display: 'flex', alignItems: 'flex-end', padding: '20px' }}>
              <p style={{ fontSize: '11px', fontWeight: 700, letterSpacing: '0.1em', lineHeight: 1.5, color: V.ink, textTransform: 'uppercase', margin: 0 }}>{item.title}</p>
            </div>
          </a>
        ))}
      </div>
    </section>
  );
}

/* ═══════════════════════════════════════════════════════════════
   9. REVIEWS
   ═══════════════════════════════════════════════════════════════ */
function ReviewsSection() {
  const reviews = [
    { quote: "Faraja Media didn't just film our event — they understood it. The angles they chose, the moments they anticipated. It was as if they could feel what mattered most to our family.", name: 'Grace W.', loc: 'Family of the late John Waweru, Westlands Nairobi' },
    { quote: "I watched my mother's memorial from Toronto in crystal-clear quality. For a moment, I forgot I was 12,000 kilometers away. That is the power of what Faraja does.", name: 'James K.', loc: 'Attended via live stream from Toronto, Canada' },
    { quote: "The drone footage was breathtaking, but it was the small details that moved us — the way they captured my grandmother's hands, the children playing, the quiet moments.", name: 'Njeri M.', loc: 'Drone & photography coverage, Kiambu Kenya' },
  ];

  return (
    <section style={{ padding: '120px 0', background: V.surface, borderTop: `1px solid ${V.border}`, borderBottom: `1px solid ${V.border}`, marginBottom: '120px' }}>
      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 24px' }}>
        <div style={{ textAlign: 'center', marginBottom: '64px' }}>
          <Eyebrow>Testimonials</Eyebrow>
          <SectionHeading center>The Trust <em>Engine</em></SectionHeading>
          <p style={{ fontSize: '16px', color: V.ink2, maxWidth: '480px', margin: '0 auto', lineHeight: 1.8 }}>Real stories from families who experienced the Faraja difference.</p>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '20px' }}>
          {reviews.map(r => (
            <div key={r.name} style={{ background: V.card, border: `1px solid ${V.border}`, borderRadius: '24px', padding: '32px 28px', display: 'flex', flexDirection: 'column' }}>
              <div style={{ display: 'flex', gap: '3px', marginBottom: '20px' }}>
                {[...Array(5)].map((_, i) => <Star key={i} size={14} fill={V.gold} color={V.gold} />)}
              </div>
              <blockquote style={{ fontFamily: V.fontDisplay, fontSize: '18px', color: V.ink, fontStyle: 'italic', lineHeight: 1.65, margin: '0 0 24px', flex: 1 }}>
                "{r.quote}"
              </blockquote>
              <div style={{ borderTop: `1px solid ${V.border}`, paddingTop: '16px' }}>
                <p style={{ fontSize: '13px', fontWeight: 600, color: V.ink, margin: '0 0 2px' }}>{r.name}</p>
                <p style={{ fontSize: '11px', color: V.ink3, margin: 0 }}>{r.loc}</p>
              </div>
            </div>
          ))}
        </div>

        <div style={{ textAlign: 'center', marginTop: '48px' }}>
          <a href={SOCIAL_LINKS.googleReview} target="_blank" rel="noopener noreferrer" style={{
            display: 'inline-flex', alignItems: 'center', gap: '8px',
            color: V.gold, fontSize: '11px', fontWeight: 700, letterSpacing: '0.25em',
            textTransform: 'uppercase', textDecoration: 'none',
            borderBottom: `1px solid ${V.goldBorder}`, paddingBottom: '4px',
          }}>
            View all 500+ reviews on Google <ArrowRight size={13} />
          </a>
        </div>
      </div>
    </section>
  );
}

/* ═══════════════════════════════════════════════════════════════
   10. FAQ
   ═══════════════════════════════════════════════════════════════ */
function FAQSection() {
  const [open, setOpen] = useState<number | null>(null);

  return (
    <section style={{ maxWidth: '800px', margin: '0 auto', padding: '0 24px 120px' }}>
      <div style={{ textAlign: 'center', marginBottom: '56px' }}>
        <Eyebrow>Common Questions</Eyebrow>
        <SectionHeading center>Frequently asked <em>questions</em></SectionHeading>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        {HOME_FAQS.map((faq, i) => (
          <div key={i} style={{
            background: V.card, border: `1px solid ${open === i ? V.goldBorder : V.border}`,
            borderRadius: '16px', overflow: 'hidden', transition: 'border-color 0.2s',
          }}>
            <button
              onClick={() => setOpen(open === i ? null : i)}
              style={{
                width: '100%', display: 'flex', alignItems: 'center',
                justifyContent: 'space-between', padding: '20px 24px',
                background: 'transparent', border: 'none', cursor: 'pointer', textAlign: 'left', gap: '16px',
              }}
            >
              <span style={{ fontSize: '15px', fontWeight: 500, color: open === i ? V.gold : V.ink, lineHeight: 1.4, transition: 'color 0.2s' }}>
                {faq.q}
              </span>
              <ChevronDown size={18} color={V.gold} style={{ flexShrink: 0, transition: 'transform 0.2s', transform: open === i ? 'rotate(180deg)' : 'rotate(0deg)' }} />
            </button>
            <AnimatePresence>
              {open === i && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.25 }}
                  style={{ overflow: 'hidden' }}
                >
                  <p style={{ padding: '0 24px 20px', fontSize: '14px', color: V.ink2, lineHeight: 1.8, margin: 0 }}>{faq.a}</p>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        ))}
      </div>
    </section>
  );
}

/* ═══════════════════════════════════════════════════════════════
   11. AREAS SERVED
   ═══════════════════════════════════════════════════════════════ */
function AreasServed() {
  const areas = [
    { region: 'Nairobi', locs: ['Westlands', 'Karen', 'Kilimani', 'Lavington', 'Langata', 'Eastleigh', 'CBD'] },
    { region: 'Central Kenya', locs: ['Kiambu', 'Thika', 'Nyeri', "Murang'a", 'Limuru', 'Gatundu'] },
    { region: 'Rift Valley', locs: ['Nakuru', 'Eldoret', 'Naivasha', 'Kericho', 'Nanyuki'] },
    { region: 'Coast', locs: ['Mombasa', 'Malindi', 'Kilifi', 'Diani', 'Lamu'] },
    { region: 'Western Kenya', locs: ['Kisumu', 'Kakamega', 'Bungoma', 'Busia', 'Siaya'] },
    { region: 'Global Diaspora', locs: ['United Kingdom', 'United States', 'Canada', 'Australia', 'Germany', 'UAE'] },
  ];

  return (
    <section style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 24px 120px' }}>
      <div style={{ textAlign: 'center', marginBottom: '56px' }}>
        <Eyebrow>Service Coverage</Eyebrow>
        <SectionHeading center>Wherever you are in Kenya —<br /><em style={{ color: V.gold }}>we'll be there.</em></SectionHeading>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: '16px' }}>
        {areas.map((area) => (
          <div key={area.region} style={{ background: V.card, border: `1px solid ${V.border}`, borderRadius: '20px', padding: '24px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '14px' }}>
              <MapPin size={14} color={V.gold} />
              <span style={{ fontSize: '14px', fontWeight: 600, color: V.ink }}>{area.region}</span>
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
              {area.locs.map(loc => (
                <span key={loc} style={{
                  fontSize: '12px', color: V.ink3,
                  background: V.surface,
                  border: `1px solid ${V.border}`,
                  padding: '4px 10px', borderRadius: '20px',
                }}>{loc}</span>
              ))}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

/* ═══════════════════════════════════════════════════════════════
   12. FINAL CTA
   ═══════════════════════════════════════════════════════════════ */
function FinalCTA() {
  return (
    <section style={{ maxWidth: '900px', margin: '0 auto', padding: '0 24px 160px', textAlign: 'center' }}>
      <motion.div
        initial={{ opacity: 0, scale: 0.94 }}
        whileInView={{ opacity: 1, scale: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
      >
        <Eyebrow>Get Started</Eyebrow>
        <h2 style={{ fontFamily: V.fontDisplay, fontSize: 'clamp(3rem, 8vw, 7rem)', fontWeight: 500, color: V.ink, lineHeight: 0.9, letterSpacing: '-0.01em', margin: '0 0 24px' }}>
          Ready to<br /><em style={{ color: V.gold }}>Be Present?</em>
        </h2>
        <p style={{ fontSize: '18px', color: V.ink2, maxWidth: '520px', margin: '0 auto 40px', lineHeight: 1.75 }}>
          No payment required to check availability. We respond within 10 minutes, 24/7.
        </p>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px', justifyContent: 'center' }}>
          <a
            href={WHATSAPP_LINK}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              display: 'inline-flex', alignItems: 'center', gap: '8px',
              background: V.gold, color: V.bg, padding: '18px 40px',
              borderRadius: '8px', textDecoration: 'none', fontSize: '12px',
              fontWeight: 700, letterSpacing: '0.15em', textTransform: 'uppercase',
            }}
          >
            Check Availability Now <ArrowRight size={14} />
          </a>
          <a
            href={`tel:${PHONE.replace(/\s/g, '')}`}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: '10px',
              background: V.wa, color: '#fff', padding: '18px 36px',
              borderRadius: '8px', textDecoration: 'none', fontSize: '12px',
              fontWeight: 700, letterSpacing: '0.15em', textTransform: 'uppercase',
            }}
          >
            <Phone size={14} /> Call Now
          </a>
        </div>
      </motion.div>
    </section>
  );
}

/* ═══════════════════════════════════════════════════════════════
   13. WHATSAPP FLOAT
   ═══════════════════════════════════════════════════════════════ */
function WhatsAppFloat() {
  const [show, setShow] = useState(false);
  const [hovered, setHovered] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setShow(true), 2200);
    return () => clearTimeout(t);
  }, []);
  if (!show) return null;
  return (
    <AnimatePresence>
      {show && (
        <motion.a
          href={WHATSAPP_LINK}
          target="_blank"
          rel="noopener noreferrer"
          initial={{ opacity: 0, y: 20, scale: 0.9 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 20, scale: 0.9 }}
          aria-label="Chat with us on WhatsApp"
          transition={{ type: 'spring', stiffness: 300, damping: 24 }}
          onMouseEnter={() => setHovered(true)}
          onMouseLeave={() => setHovered(false)}
          style={{
            position: 'fixed', bottom: '28px', right: '24px', zIndex: 500,
            display: 'flex', alignItems: 'center', gap: '10px',
            background: V.wa, color: '#fff', borderRadius: '50px',
            padding: hovered ? '14px 22px 14px 18px' : '14px',
            minWidth: '56px', minHeight: '56px',
            textDecoration: 'none',
            boxShadow: '0 6px 28px rgba(37,211,102,0.3)',
            transition: 'all 0.2s',
          }}
        >
          <WhatsAppIcon size={24} color="#fff" />
          <AnimatePresence>
            {hovered && (
              <motion.span
                initial={{ opacity: 0, width: 0 }}
                animate={{ opacity: 1, width: 'auto' }}
                exit={{ opacity: 0, width: 0 }}
                style={{ fontSize: '13px', fontWeight: 700, whiteSpace: 'nowrap', letterSpacing: '0.04em', overflow: 'hidden' }}
              >
                Chat with us
              </motion.span>
            )}
          </AnimatePresence>
        </motion.a>
      )}
    </AnimatePresence>
  );
}

/* ═══════════════════════════════════════════════════════════════
   HOME PAGE — EXPORT
   ═══════════════════════════════════════════════════════════════ */
export default function Home() {
  const homeSchema = [
    {
      "@type": "FuneralHome",
      "name": "Faraja Media",
      "telephone": PHONE,
      "priceRange": "KES 20,000 - KES 150,000",
      "openingHours": "Mo-Su 00:00-23:59",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "Westlands",
        "addressLocality": "Nairobi",
        "addressCountry": "KE"
      },
      "areaServed": [
        { "@type": "City", "name": "Nairobi" },
        { "@type": "Country", "name": "Kenya" },
        { "@type": "Country", "name": "United Kingdom" },
        { "@type": "Country", "name": "United States" },
        { "@type": "Country", "name": "Canada" }
      ]
    },
    {
      "@type": "FAQPage",
      "mainEntity": HOME_FAQS.map((faq) => ({
        "@type": "Question",
        "name": faq.q,
        "acceptedAnswer": {
          "@type": "Answer",
          "text": faq.a
        }
      }))
    }
  ];

  return (
    <>
      <SEO
        title={HOME_SEO.title}
        description={HOME_SEO.description}
        ogImage={HOME_SEO.ogImage}
        serviceType={HOME_SEO.serviceType}
        breadcrumbs={HOME_SEO.breadcrumbs}
        schema={homePageSchema}
      />
      <Helmet>
        {/* Hero preload optimized in index.html for critical path performance */}
      </Helmet>

      <div style={{ background: V.bg, minHeight: '100vh', color: V.ink }}>
        <UrgencyBar />
        <HeroSection />
        <TrustStats />
        <ServicesSection />
        <DiasporaSection />
        <DiasporaGuide />
        <PackageBuilder />
        <PortfolioSection />
        <ReviewsSection />
        <FAQSection />
        <AreasServed />
        <FinalCTA />
        <WhatsAppFloat />
      </div>
    </>
  );
}
